import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader from 'components/PageSections/components/SectionHeader';
import ShapeBanner, {
  ShapeBannerProduct,
  ShapeBannerProps,
} from 'components/ShapeBanner';
import TabbedAccordion, {
  TabbedAccordionElement,
} from 'components/TabbedAccordion';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import ewAnalytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

export type TabbedBannersProps = {
  eyebrow?: string;
  title?: string;
  shapeBannersData?: ShapeBannerProps[];
  productSlugs?: string[][] | null;
};

/**
 * TabbedBanners is a component that displays a list of ShapeBanners in a tabbed accordion
 * @param {TabbedBannersProps} props The props for the component.
 * @returns {JSX.Element} The TabbedBanners component.
 *
 */
function TabbedBanners({
  eyebrow,
  title,
  shapeBannersData,
  productSlugs,
}: TabbedBannersProps): JSX.Element {
  const tabElements: TabbedAccordionElement[] = [];
  const productsBySlug: ProductBySlug = useProducts();

  shapeBannersData?.forEach((shapeBannerData, index) => {
    let shapeBannerProducts: ShapeBannerProduct[] = [];

    if (productSlugs && productSlugs[index]) {
      shapeBannerProducts = productSlugs[index].map(
        (slug) => productsBySlug[slug],
      );
    }

    tabElements.push({
      label: shapeBannerData.title || '',
      content: (
        <ShapeBanner {...shapeBannerData} products={shapeBannerProducts} />
      ),
    });
  });

  const handleTabClick = (label: string) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'Tabbed Banners',
        ctaText: label,
      },
    });
  };

  return (
    <SectionContainer size={['sm', null, 'xl']}>
      <SectionHeader eyebrow={eyebrow} title={title}></SectionHeader>

      <TabbedAccordion
        tabs={tabElements}
        switchToTabBreakpoint="desktop"
        activeBackgroundColor="viridian.base"
        onClickHeader={handleTabClick}
      ></TabbedAccordion>
    </SectionContainer>
  );
}

export default TabbedBanners;

type ProductBySlug = Record<string, ShapeBannerProduct>;

const useProducts = (): ProductBySlug => {
  const { allSpreeProduct, allContentfulProduct } = useStaticQuery(graphql`
    query {
      allContentfulProduct {
        nodes {
          displayName
          slug
        }
      }
      allSpreeProduct {
        nodes {
          slug
          contentfulProductInfo {
            productCardImage {
              gatsbyImageData(placeholder: BLURRED)
            }
          }
          variants {
            price
            is_master
          }
        }
      }
    }
  `);

  const contentfulProductSlugWithName: Record<string, string> = {};
  // Create a map allContentfulProduct nodes by slug
  allContentfulProduct?.nodes?.forEach((product: any) => {
    if (!product.slug) {
      return;
    }

    contentfulProductSlugWithName[product.slug] = product.displayName;
  });

  const spreeProductsBySlug: ProductBySlug = {};

  allSpreeProduct?.nodes?.forEach((product: any) => {
    const masterPrice = product?.variants?.find(
      (variant: any) => variant?.is_master,
    )?.price;

    if (
      !product.slug ||
      !masterPrice ||
      !product?.contentfulProductInfo?.productCardImage
    ) {
      return;
    }

    spreeProductsBySlug[product.slug] = {
      slug: product?.slug,
      displayName: contentfulProductSlugWithName[product.slug],
      price: masterPrice,
      boxImage: product?.contentfulProductInfo?.productCardImage,
    };
  });

  return spreeProductsBySlug;
};
